import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const schema = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Supplements for Men Over 60: What Works and What's a Waste",
    "author": { "@type": "Person", "name": "Mason Hale" },
    "datePublished": "2026-01-18",
    "dateModified": "2026-04-06",
    "publisher": { "@type": "Organization", "name": "Mason Hale Fitness" },
    "url": "https://masonhalefit.com/blog/supplements-for-men-over-60"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What supplements should men over 60 take?",
        "acceptedAnswer": { "@type": "Answer", "text": "The evidence-backed supplements for men over 60 are: creatine monohydrate, vitamin D3 + K2, omega-3 fish oil, magnesium glycinate, and protein powder if dietary intake is insufficient. Everything else is largely optional." }
      },
      {
        "@type": "Question",
        "name": "Is vitamin D important for men over 60?",
        "acceptedAnswer": { "@type": "Answer", "text": "Extremely. Vitamin D deficiency is common in older men and linked to muscle weakness, bone loss, depression, and immune dysfunction. Most men over 60 benefit from 2,000-4,000 IU of D3 daily, ideally taken with K2." }
      },
      {
        "@type": "Question",
        "name": "Are testosterone boosters worth it for men over 60?",
        "acceptedAnswer": { "@type": "Answer", "text": "No. The vast majority of over-the-counter testosterone boosters have no meaningful clinical evidence. If you're concerned about low testosterone, see a doctor for proper testing and treatment options." }
      },
      {
        "@type": "Question",
        "name": "How much omega-3 should men over 60 take?",
        "acceptedAnswer": { "@type": "Answer", "text": "1-3 grams of combined EPA and DHA per day is the typical research-backed range. Look for a fish oil supplement that lists EPA and DHA amounts separately, not just 'fish oil' content." }
      }
    ]
  }
]);

const tocItems = [
  { id: 'the-truth', label: 'The Truth About Supplements' },
  { id: 'tier-1', label: 'Tier 1: The Essentials (Strong Evidence)' },
  { id: 'tier-2', label: 'Tier 2: Worth Considering' },
  { id: 'waste-of-money', label: 'Complete Waste of Money' },
  { id: 'how-to-start', label: 'How to Start Without Overwhelming Yourself' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const related = [
  { slug: 'creatine-for-men-over-60', title: 'Creatine for Men Over 60: Is It Safe?', category: 'Nutrition' },
  { slug: 'protein-intake-for-men-over-60', title: 'How Much Protein Do Men Over 60 Actually Need?', category: 'Nutrition' },
  { slug: 'best-protein-powder-for-men-over-60', title: 'Best Protein Powder for Men Over 60 (2026 Guide)', category: 'Nutrition' },
];

export default function SupplementsPage() {
  return (
    <BlogLayout
      title="Supplements for Men Over 60: What Works vs. Waste"
      description="Honest breakdown of supplements for men over 60. What the science actually supports, what's a waste of money, and the 5 worth taking."
      keywords="supplements for men over 60, best supplements for older men, vitamins for men over 60"
      canonicalPath="/blog/supplements-for-men-over-60"
      datePublished="2026-01-18"
      dateModified="2026-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Supplements for Men Over 60: What Works and What's a Waste" />

      <article>
        <header className="mb-8">
          <span className="text-[#c8a96e] text-xs font-bold tracking-[0.25em] uppercase">Nutrition</span>
          <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mt-2 mb-4">
            Supplements for Men Over 60: What Works and What's a Waste
          </h1>
          <p className="text-gray-400 text-base leading-relaxed">
            The supplement industry is a $50 billion business built largely on hype, hope, and clever marketing. Most of what's on the shelf is either useless or redundant if you're eating well.
          </p>
          <p className="text-gray-400 text-base leading-relaxed mt-3">
            But a handful of supplements have real, peer-reviewed evidence behind them — especially for men over 60. Here's the honest breakdown.
          </p>
        </header>

        <TableOfContents items={tocItems} />

        <section id="the-truth" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">The Truth About Supplements</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            Supplements are exactly that — supplements to a solid diet and training program. They don't replace good nutrition. They don't replace consistent exercise. They fill specific gaps.
          </p>
          <p className="text-gray-400 leading-relaxed mb-3">
            The <a href="https://www.nia.nih.gov/health/vitamins-and-minerals/vitamins-and-minerals-older-adults" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:underline">National Institute on Aging</a> notes that while some supplements are beneficial for older adults, many are unnecessary and some can even be harmful in high doses.
          </p>
          <p className="text-gray-400 leading-relaxed mb-4">
            Always consult your doctor before starting any new supplement, especially if you're on medications. Some supplements interact with common prescriptions.
          </p>
        </section>

        <section id="tier-1" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Tier 1: The Essentials (Strong Evidence)</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            These five supplements have solid research behind them for men over 60 who strength train:
          </p>

          <div className="space-y-4 mb-6">
            {[
              {
                name: 'Creatine Monohydrate',
                dose: '3-5g daily',
                why: 'The most researched supplement in existence. Increases strength, power output, and muscle mass. Also has emerging evidence for cognitive benefits. Safe for long-term use.',
                note: 'Take it daily — timing doesn\'t matter much. No loading phase needed.'
              },
              {
                name: 'Vitamin D3 + K2',
                dose: '2,000-4,000 IU D3 + 100mcg K2',
                why: 'Vitamin D deficiency is epidemic in older men. Critical for muscle function, bone density, immune health, and testosterone production. K2 ensures calcium goes to bones, not arteries.',
                note: 'Take with a fat-containing meal for best absorption.'
              },
              {
                name: 'Omega-3 Fish Oil',
                dose: '1-3g EPA+DHA daily',
                why: 'Reduces inflammation, supports heart health, improves joint comfort, and has evidence for reducing muscle soreness. Critical for men over 60 with joint issues.',
                note: 'Look for products listing EPA and DHA amounts separately.'
              },
              {
                name: 'Magnesium Glycinate',
                dose: '200-400mg before bed',
                why: 'Most men over 60 are deficient. Supports sleep quality, muscle recovery, blood pressure regulation, and insulin sensitivity. Glycinate form is best absorbed and gentlest on digestion.',
                note: 'Avoid magnesium oxide — it\'s poorly absorbed and causes digestive issues.'
              },
              {
                name: 'Protein Powder (Whey or Plant)',
                dose: '25-50g daily as needed',
                why: 'Not a supplement in the traditional sense, but a convenient way to hit protein targets. Essential if you\'re struggling to get 0.7-1.0g/lb from whole foods.',
                note: 'Use to supplement diet, not replace whole food protein.'
              },
            ].map(({ name, dose, why, note }) => (
              <div key={name} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white font-bold text-base">{name}</p>
                  <span className="text-[#c8a96e] text-xs font-bold bg-[#c8a96e]/10 px-2 py-1 rounded whitespace-nowrap ml-3">{dose}</span>
                </div>
                <p className="text-gray-400 text-sm mb-2">{why}</p>
                <p className="text-gray-500 text-xs italic">{note}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="tier-2" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Tier 2: Worth Considering</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            These have decent evidence but are more situational:
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-3 mb-4 ml-2">
            <li><strong className="text-white">Zinc:</strong> Supports testosterone production and immune function. Many older men are deficient. 15-30mg daily.</li>
            <li><strong className="text-white">Collagen peptides:</strong> May support joint health and connective tissue. Take with vitamin C for best effect. Not a muscle-building protein.</li>
            <li><strong className="text-white">Ashwagandha:</strong> Adaptogen with evidence for reducing cortisol and modestly improving testosterone. 300-600mg KSM-66 extract.</li>
            <li><strong className="text-white">Berberine:</strong> Strong evidence for blood sugar regulation. Relevant for men with pre-diabetes or metabolic syndrome.</li>
            <li><strong className="text-white">CoQ10:</strong> Especially relevant if you're on statins, which deplete CoQ10. Supports energy production and heart health.</li>
          </ul>
          <p className="text-gray-400 leading-relaxed">
            The <a href="https://www.mayoclinic.org/healthy-lifestyle/nutrition-and-healthy-eating/in-depth/supplements/art-20044894" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:underline">Mayo Clinic</a> recommends discussing all supplements with your healthcare provider, particularly if you're managing chronic conditions.
          </p>
        </section>

        <section id="waste-of-money" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Complete Waste of Money</h2>
          <ul className="list-disc list-inside text-gray-400 space-y-2 mb-4 ml-2">
            <li><strong className="text-white">Testosterone boosters:</strong> No meaningful clinical evidence. If you have low T, see a doctor.</li>
            <li><strong className="text-white">Fat burners:</strong> Mostly caffeine with a markup. The "thermogenic" effect is minimal.</li>
            <li><strong className="text-white">BCAAs (if you're already eating enough protein):</strong> Redundant. You get plenty of BCAAs from whole food protein and whey.</li>
            <li><strong className="text-white">Proprietary blends:</strong> When they hide ingredient amounts, they're hiding underdosing.</li>
            <li><strong className="text-white">Most "anti-aging" supplements:</strong> Resveratrol, NMN, etc. — interesting science, but not proven in humans at scale yet.</li>
            <li><strong className="text-white">Collagen as a primary protein source:</strong> Not a complete protein. Won't build muscle.</li>
          </ul>
        </section>

        <section id="how-to-start" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">How to Start Without Overwhelming Yourself</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            Don't try to add five supplements at once. Start with the two most impactful for men over 60 who strength train:
          </p>
          <ol className="list-decimal list-inside text-gray-400 space-y-2 mb-4 ml-2">
            <li><strong className="text-white">Creatine monohydrate</strong> — 5g daily, any time</li>
            <li><strong className="text-white">Vitamin D3 + K2</strong> — with breakfast</li>
          </ol>
          <p className="text-gray-400 leading-relaxed mb-3">
            Run those for 4-6 weeks. Then add magnesium glycinate before bed. Then fish oil. Build the habit before adding complexity.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Supplements are a small piece of the puzzle. The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:underline font-semibold">Over-60 Strength Blueprint</a> gives you the training and nutrition foundation that makes supplements actually work — because supplements on top of a bad program do nothing.
          </p>
        </section>

        <section id="faq" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {[
              { q: "What supplements should men over 60 take?", a: "The evidence-backed supplements for men over 60 are: creatine monohydrate, vitamin D3 + K2, omega-3 fish oil, magnesium glycinate, and protein powder if dietary intake is insufficient." },
              { q: "Is vitamin D important for men over 60?", a: "Extremely. Vitamin D deficiency is common in older men and linked to muscle weakness, bone loss, depression, and immune dysfunction. Most men over 60 benefit from 2,000-4,000 IU of D3 daily, ideally taken with K2." },
              { q: "Are testosterone boosters worth it for men over 60?", a: "No. The vast majority of over-the-counter testosterone boosters have no meaningful clinical evidence. If you're concerned about low testosterone, see a doctor for proper testing and treatment options." },
              { q: "How much omega-3 should men over 60 take?", a: "1-3 grams of combined EPA and DHA per day is the typical research-backed range. Look for a fish oil supplement that lists EPA and DHA amounts separately." },
            ].map(({ q, a }) => (
              <div key={q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                <p className="text-white font-bold mb-2">{q}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 bg-[#111111] border border-[#c8a96e]/20 rounded-lg p-6">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-2">The Foundation Matters Most</p>
          <p className="text-white font-bold text-lg mb-2">Get The Over-60 Strength Blueprint</p>
          <p className="text-gray-400 text-sm mb-4">The training and nutrition system that makes supplements actually work. Built for men over 60 by a man who's done it himself.</p>
          <a href="https://masonhalefit.com" className="inline-flex items-center gap-2 px-6 py-3 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
            Get The Program — $19.99
          </a>
        </div>

        <AuthorBio />
        <RelatedArticles articles={related} />
      </article>
    </BlogLayout>
  );
}
